import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { RefreshCw, Lock, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("If an account exists, a reset link has been sent");
    } catch (error) {
      toast.error("Failed to process request");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="w-[400px] bg-zinc-900/50 border-white/5">
            <CardContent className="p-8 text-center">
              <CheckCircle className="mx-auto text-emerald-400 mb-4" size={48} />
              <h2 className="text-xl font-bold text-white mb-2">Check Your Email</h2>
              <p className="text-zinc-400 mb-6">
                If an account with <span className="text-blue-400">{email}</span> exists, we've sent a password reset link.
              </p>
              <p className="text-zinc-500 text-sm mb-6">The link will expire in 15 minutes.</p>
              <Button onClick={() => navigate("/login")} variant="outline" className="border-zinc-700 text-zinc-300">
                <ArrowLeft size={16} className="mr-2" />Back to Login
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="w-[400px] bg-zinc-900/50 border-white/5">
          <CardHeader className="text-center pb-2">
            <h1 className="text-3xl font-bold text-white">q<span className="text-blue-500">Pro</span></h1>
            <p className="text-zinc-500 text-sm mt-2">Forgot Password</p>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-zinc-400 text-sm mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Email Address</label>
                  <Input 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    type="email" 
                    placeholder="your@email.com" 
                    className="bg-zinc-800 border-zinc-700 text-white" 
                    required 
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500">
                  {loading ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Lock size={16} className="mr-2" />}
                  Send Reset Link
                </Button>
              </div>
            </form>
            <div className="mt-4 text-center">
              <button onClick={() => navigate("/login")} className="text-xs text-zinc-500 hover:text-blue-400">
                Back to login
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

export function ResetPasswordWithTokenPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/verify-reset-token/${token}`)
        .then((res) => {
          setValid(true);
          setEmail(res.data.email);
        })
        .catch(() => {
          setValid(false);
          toast.error("Invalid or expired reset link");
        })
        .finally(() => setVerifying(false));
    } else {
      setVerifying(false);
      setValid(false);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password-with-token`, { token, new_password: newPassword });
      setSuccess(true);
      toast.success("Password reset successfully");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to reset password";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="w-[400px] bg-zinc-900/50 border-white/5">
            <CardContent className="p-8 text-center">
              <CheckCircle className="mx-auto text-emerald-400 mb-4" size={48} />
              <h2 className="text-xl font-bold text-white mb-2">Password Reset</h2>
              <p className="text-zinc-400 mb-6">Your password has been reset successfully.</p>
              <Button onClick={() => navigate("/login")} className="bg-blue-600 hover:bg-blue-500">
                Sign In
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="w-[400px] bg-zinc-900/50 border-white/5">
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <Lock className="text-red-400" size={24} />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Invalid Link</h2>
              <p className="text-zinc-400 mb-6">This password reset link is invalid or has expired.</p>
              <Button onClick={() => navigate("/forgot-password")} variant="outline" className="border-zinc-700 text-zinc-300">
                Request New Link
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090B] p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="w-[400px] bg-zinc-900/50 border-white/5">
          <CardHeader className="text-center pb-2">
            <h1 className="text-3xl font-bold text-white">q<span className="text-blue-500">Pro</span></h1>
            <p className="text-zinc-500 text-sm mt-2">Reset Password</p>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-zinc-400 text-sm mb-6">
              Enter a new password for <span className="text-blue-400">{email}</span>
            </p>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">New Password</label>
                  <Input 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    type="password" 
                    placeholder="********" 
                    className="bg-zinc-800 border-zinc-700 text-white" 
                    required 
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Confirm Password</label>
                  <Input 
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)} 
                    type="password" 
                    placeholder="********" 
                    className="bg-zinc-800 border-zinc-700 text-white" 
                    required 
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500">
                  {loading ? <RefreshCw className="animate-spin mr-2" size={16} /> : <Lock size={16} className="mr-2" />}
                  Reset Password
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
